/**
 * HierarchyTab - List and Chart views of supervision hierarchy
 */

import { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { List, GitFork } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import memberService, { HierarchyMember } from '../../services/api/memberService';
import { useAuthStore } from '../../stores/authStore';
import { extractApiError } from '../../utils/apiError';
import { buildSubtreeIndex } from './supervisionSubtree';

// ============================================================================
// Styled Components
// ============================================================================

const ToggleBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary.base : theme.colors.borders.light};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.status.info.bg : theme.colors.backgrounds.white};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary.base : theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
    color: ${({ theme }) => theme.colors.primary.base};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ---- LIST VIEW ----

const TableWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.page};
`;

const Th = styled.th`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:last-child td {
    border-bottom: none;
  }

  &:hover td {
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }
`;

const SupervisorSelect = styled.select`
  height: 36px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

/** Stacks the select and its rejection message inside the "Supervised By" cell. */
const SupervisorCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  /* Cap the width so a long server message wraps inside the cell instead of
     widening the table and pushing the page into horizontal overflow. */
  max-width: 260px;
`;

/** Inline 400 from the server (e.g. the #505 cycle rejection). Mirrors the error
 *  styling EditMemberModal already uses. */
const RowError = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const SupervisesText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Footer = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

// ---- CHART VIEW ----

const ChartContainer = styled.div`
  overflow: auto;
  padding: ${({ theme }) => theme.spacing[8]};
`;

const ChartTree = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
`;

const ChartLevel = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[8]};
`;

const ChartNodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConnectorDown = styled.div`
  width: 1px;
  height: 32px;
  background: ${({ theme }) => theme.colors.borders.medium};
`;


const ChartNode = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[4]};
  border: 1px solid ${({ theme }) => theme.colors.primary.base};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  cursor: pointer;
  width: 128px;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.status.info.bg};
    border-color: ${({ theme }) => theme.colors.primary.sky};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const NodeInitials = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.primary.base};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.primary.base};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NodeName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

const NodeRole = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

const ChartHint = styled.p`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing[8]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[12]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// ============================================================================
// Helpers
// ============================================================================

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

type TreeNode = HierarchyMember & { children: TreeNode[] };

function buildTree(members: HierarchyMember[]): TreeNode[] {
  const nodeMap = new Map<string, TreeNode>();
  members.forEach((m) => nodeMap.set(m.id, { ...m, children: [] }));

  const roots: TreeNode[] = [];
  members.forEach((m) => {
    const node = nodeMap.get(m.id)!;
    if (m.supervisorId && nodeMap.has(m.supervisorId)) {
      nodeMap.get(m.supervisorId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

// ============================================================================
// Sub-components
// ============================================================================

interface ChartNodeGroupProps {
  nodes: TreeNode[];
}

function ChartNodeGroup({ nodes }: ChartNodeGroupProps) {
  if (nodes.length === 0) return null;

  return (
    <ChartLevel>
      {nodes.map((node) => (
        <ChartNodeWrapper key={node.id}>
          <ChartNode>
            <NodeInitials>{getInitials(node.firstName, node.lastName)}</NodeInitials>
            <NodeName>
              {node.firstName} {node.lastName.charAt(0)}.
            </NodeName>
            <NodeRole>{node.roles[0]?.name ?? '—'}</NodeRole>
          </ChartNode>
          {node.children.length > 0 && (
            <>
              <ConnectorDown />
              <ChartNodeGroup nodes={node.children} />
            </>
          )}
        </ChartNodeWrapper>
      ))}
    </ChartLevel>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export const HierarchyTab = () => {
  const [view, setView] = useState<'list' | 'chart'>('list');
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['hierarchy'],
    queryFn: () => memberService.getHierarchy(),
  });

  // Rejection reason per row. Keyed by member, like `pendingSupervisor` — rows are
  // independently editable, so a single slot would let one row's failure silently
  // overwrite another's message.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  // Optimistic select values, keyed by member. Held only while the request is in flight
  // so the select reflects the user's choice; cleared on BOTH outcomes, which is what
  // reverts a rejected change back to the persisted supervisor.
  const [pendingSupervisor, setPendingSupervisor] = useState<Record<string, string>>({});

  // Latest request number issued per row. A row can be changed again before its previous
  // request settles; without this, the stale callback would clear the newer optimistic
  // value (snapping the select back) or write an error for a change already superseded.
  const latestRequestRef = useRef<Record<string, number>>({});

  const clearPending = (memberId: string) =>
    setPendingSupervisor((prev) => {
      const next = { ...prev };
      delete next[memberId];
      return next;
    });

  /** True when this callback belongs to the most recent request for its row. */
  const isCurrent = (memberId: string, requestId: number) =>
    latestRequestRef.current[memberId] === requestId;

  const updateSupervisorMutation = useMutation({
    mutationFn: ({
      memberId,
      supervisorId,
    }: {
      memberId: string;
      supervisorId: string | null;
      requestId: number;
    }) => memberService.updateSupervisor(memberId, supervisorId),
    onSuccess: (_result, variables) => {
      if (!isCurrent(variables.memberId, variables.requestId)) return;
      clearPending(variables.memberId);
      setRowErrors((prev) => {
        if (!(variables.memberId in prev)) return prev;
        const next = { ...prev };
        delete next[variables.memberId];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
    },
    onError: (error, variables) => {
      if (!isCurrent(variables.memberId, variables.requestId)) return;
      // Surface the SERVER's message — a cycle rejection names both people and the
      // direction of the existing relationship, which a generic string throws away.
      clearPending(variables.memberId);
      setRowErrors((prev) => ({
        ...prev,
        [variables.memberId]: extractApiError(
          error,
          'Failed to update supervisor. Please try again.',
        ),
      }));
    },
  });

  // Memoized so the `??` fallback doesn't hand `subtreeByMember` a fresh array identity
  // on every render, which would defeat its useMemo entirely.
  const members = useMemo(() => data?.members ?? [], [data]);
  const rootCount = data?.rootCount ?? 0;
  const unassignedCount = data?.unassignedCount ?? 0;

  const treeRoots = buildTree(members);

  // Descendants per member, derived from the `supervisees` edges the hierarchy payload
  // already carries. Used only to hide choices the server would reject — the backend
  // guard remains authoritative.
  const subtreeByMember = useMemo(() => buildSubtreeIndex(members), [members]);

  const handleSupervisorChange = (memberId: string, newSupervisorId: string) => {
    const requestId = (latestRequestRef.current[memberId] ?? 0) + 1;
    latestRequestRef.current[memberId] = requestId;
    setPendingSupervisor((prev) => ({ ...prev, [memberId]: newSupervisorId }));
    updateSupervisorMutation.mutate({
      memberId,
      supervisorId: newSupervisorId === '' ? null : newSupervisorId,
      requestId,
    });
  };

  if (isLoading) {
    return <EmptyState>Loading hierarchy...</EmptyState>;
  }

  if (members.length === 0) {
    return <EmptyState>No members in hierarchy</EmptyState>;
  }

  return (
    <>
      <ToggleBar>
        <ToggleBtn $active={view === 'list'} onClick={() => setView('list')}>
          <List />
          List
        </ToggleBtn>
        <ToggleBtn $active={view === 'chart'} onClick={() => setView('chart')}>
          <GitFork />
          Chart
        </ToggleBtn>
      </ToggleBar>

      {view === 'list' ? (
        <TableWrapper>
          <SectionHeader>Supervision Hierarchy</SectionHeader>
          <Table>
            <TableHead>
              <tr>
                <Th>Clinician</Th>
                <Th>Role</Th>
                <Th>Supervised By</Th>
                <Th>Supervises</Th>
              </tr>
            </TableHead>
            <tbody>
              {members.map((member) => {
                const supervisees = member.supervisees ?? [];
                return (
                  <Tr key={member.id}>
                    <Td>
                      {member.firstName} {member.lastName}
                    </Td>
                    <Td>
                      {member.roles[0] ? (
                        <Badge roleName={member.roles[0].name}>{member.roles[0].name}</Badge>
                      ) : (
                        <SupervisesText>—</SupervisesText>
                      )}
                    </Td>
                    <Td>
                      <SupervisorCell>
                        <SupervisorSelect
                          value={pendingSupervisor[member.id] ?? member.supervisorId ?? ''}
                          onChange={(e) => handleSupervisorChange(member.id, e.target.value)}
                          disabled={!hasPermission('team:manage:actions')}
                        >
                          <option value="">None</option>
                          {members
                            .filter(
                              (m) =>
                                m.id !== member.id &&
                                !(subtreeByMember.get(member.id)?.has(m.id) ?? false),
                            )
                            .map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.firstName} {m.lastName}
                              </option>
                            ))}
                        </SupervisorSelect>
                        {rowErrors[member.id] && (
                          <RowError role="alert">{rowErrors[member.id]}</RowError>
                        )}
                      </SupervisorCell>
                    </Td>
                    <Td>
                      {supervisees.length > 0 ? (
                        <SupervisesText>
                          {supervisees
                            .map((s) => `${s.firstName} ${s.lastName.charAt(0)}.`)
                            .join(', ')}
                        </SupervisesText>
                      ) : (
                        <SupervisesText>--</SupervisesText>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          <Footer>
            {rootCount} root supervisor{rootCount !== 1 ? 's' : ''}, {unassignedCount} unassigned
            member{unassignedCount !== 1 ? 's' : ''}
          </Footer>
        </TableWrapper>
      ) : (
        <>
          <ChartContainer>
            <ChartTree>
              <ChartNodeGroup nodes={treeRoots} />
            </ChartTree>
          </ChartContainer>
          <ChartHint>Click any node to reassign supervision.</ChartHint>
        </>
      )}
    </>
  );
};

export default HierarchyTab;
